/**
 * ComisionamientoRepo sobre Supabase (Punch List compartido).
 *
 * Maneja 4 sub-entidades en la tabla única pmis_entities:
 *   listadoPendientes, sistemaComm, subsistemaComm, pendienteComm.
 *
 * Las cascadas de borrado se hacen en secuencia (no hay transacción atómica
 * en este patrón); aceptable para el piloto. El bloqueo optimista por
 * `version` se conserva donde el adaptador Dexie lo tenía.
 */

import type { ComisionamientoRepo } from "../../repos/ComisionamientoRepo";
import type {
  ListadoPendientes,
  PendienteComm,
  SistemaComm,
  SubsistemaComm,
} from "@app-types/entities";
import { OptimisticLockError } from "../../repos/BaseRepo";
import { supabase, TABLE, SupabaseRepoError } from "./supabaseClient";

interface BaseShape {
  id: string;
  tenantId: string;
  version: number;
  createdAt: string;
  updatedAt: string;
}

export class SupabaseComisionamientoRepo implements ComisionamientoRepo {
  constructor(private tenantId: string) {}

  // ── Helpers genéricos sobre pmis_entities ────────────────────────
  private async all<E>(entity: string): Promise<E[]> {
    const { data, error } = await supabase()
      .from(TABLE)
      .select("payload")
      .eq("entity", entity)
      .eq("tenant_id", this.tenantId);
    if (error) throw new SupabaseRepoError(error.message, `list(${entity})`);
    return ((data ?? []) as { payload: E }[]).map((r) => r.payload);
  }

  private async one<E>(entity: string, id: string): Promise<E | null> {
    const { data, error } = await supabase()
      .from(TABLE)
      .select("payload")
      .eq("entity", entity)
      .eq("tenant_id", this.tenantId)
      .eq("id", id)
      .maybeSingle();
    if (error) throw new SupabaseRepoError(error.message, `get(${entity}, ${id})`);
    return data ? (data as { payload: E }).payload : null;
  }

  private row<E extends BaseShape>(entity: string, obj: E): Record<string, unknown> {
    return {
      entity,
      id: obj.id,
      tenant_id: this.tenantId,
      version: obj.version,
      payload: obj,
      created_at: obj.createdAt,
      updated_at: obj.updatedAt,
    };
  }

  private async ins<E extends BaseShape>(entity: string, obj: E): Promise<void> {
    const { error } = await supabase().from(TABLE).insert(this.row(entity, obj));
    if (error) throw new SupabaseRepoError(error.message, `create(${entity})`);
  }

  private async put<E extends BaseShape>(entity: string, obj: E): Promise<void> {
    const { error } = await supabase()
      .from(TABLE)
      .upsert(this.row(entity, obj), { onConflict: "entity,id" });
    if (error) throw new SupabaseRepoError(error.message, `put(${entity})`);
  }

  private async del(entity: string, ids: string[]): Promise<void> {
    if (ids.length === 0) return;
    const { error } = await supabase()
      .from(TABLE)
      .delete()
      .eq("entity", entity)
      .eq("tenant_id", this.tenantId)
      .in("id", ids);
    if (error) throw new SupabaseRepoError(error.message, `delete(${entity})`);
  }

  // ── Listados ─────────────────────────────────────────────────────
  async listListados(filter?: { afeId?: string }): Promise<ListadoPendientes[]> {
    let rows = await this.all<ListadoPendientes>("listadoPendientes");
    if (filter?.afeId) rows = rows.filter((l) => l.afeId === filter.afeId);
    return rows.sort((a, b) => b.fechaApertura.localeCompare(a.fechaApertura));
  }

  async getListado(id: string): Promise<ListadoPendientes | null> {
    return this.one<ListadoPendientes>("listadoPendientes", id);
  }

  async createListado(
    data: Omit<ListadoPendientes, "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "createdBy" | "updatedBy">,
    userEmail: string,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const nuevo: ListadoPendientes = {
      ...data, id, tenantId: this.tenantId, version: 1,
      createdAt: now, updatedAt: now, createdBy: userEmail, updatedBy: userEmail,
    };
    await this.ins("listadoPendientes", nuevo);
    return id;
  }

  async updateListado(id: string, data: Partial<ListadoPendientes>, expectedVersion: number, userEmail: string): Promise<void> {
    const current = await this.getListado(id);
    if (!current) throw new Error(`Listado ${id} no encontrado`);
    if (current.version !== expectedVersion) throw new OptimisticLockError(id, expectedVersion, current.version);
    await this.put("listadoPendientes", {
      ...current, ...data, version: current.version + 1,
      updatedAt: new Date().toISOString(), updatedBy: userEmail,
    });
  }

  async firmarListado(id: string, tipo: "OPER" | "MTTO" | "COMM", userEmail: string): Promise<void> {
    const current = await this.getListado(id);
    if (!current) throw new Error(`Listado ${id} no encontrado`);
    const now = new Date().toISOString();
    const today = now.slice(0, 10);
    const patch: Partial<ListadoPendientes> = {};
    if (tipo === "OPER") { patch.firmaOperacionesEmail = userEmail; patch.firmaOperacionesFecha = today; }
    else if (tipo === "MTTO") { patch.firmaMantenimientoEmail = userEmail; patch.firmaMantenimientoFecha = today; }
    else { patch.firmaCommEmail = userEmail; patch.firmaCommFecha = today; }
    await this.put("listadoPendientes", {
      ...current, ...patch, version: current.version + 1, updatedAt: now, updatedBy: userEmail,
    });
  }

  async cerrarListado(id: string, userEmail: string): Promise<void> {
    const current = await this.getListado(id);
    if (!current) throw new Error(`Listado ${id} no encontrado`);
    if (current.fechaCierre) return;
    const now = new Date().toISOString();
    await this.put("listadoPendientes", {
      ...current, fechaCierre: now.slice(0, 10), version: current.version + 1, updatedAt: now, updatedBy: userEmail,
    });
  }

  async deleteListado(id: string): Promise<void> {
    const pendientes = (await this.all<PendienteComm>("pendienteComm")).filter((p) => p.listadoId === id);
    const sistemas = (await this.all<SistemaComm>("sistemaComm")).filter((s) => s.listadoId === id);
    const sistemaIds = sistemas.map((s) => s.id);
    const subsistemas = (await this.all<SubsistemaComm>("subsistemaComm")).filter((s) => sistemaIds.includes(s.sistemaId));
    await this.del("pendienteComm", pendientes.map((p) => p.id));
    await this.del("subsistemaComm", subsistemas.map((s) => s.id));
    await this.del("sistemaComm", sistemaIds);
    await this.del("listadoPendientes", [id]);
  }

  // ── Sistemas ─────────────────────────────────────────────────────
  async listSistemas(listadoId: string): Promise<SistemaComm[]> {
    return (await this.all<SistemaComm>("sistemaComm"))
      .filter((s) => s.listadoId === listadoId)
      .sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }));
  }

  async createSistema(
    data: Omit<SistemaComm, "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "createdBy" | "updatedBy">,
    userEmail: string,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    await this.ins("sistemaComm", {
      ...data, id, tenantId: this.tenantId, version: 1,
      createdAt: now, updatedAt: now, createdBy: userEmail, updatedBy: userEmail,
    });
    return id;
  }

  async updateSistema(id: string, data: Partial<SistemaComm>, userEmail: string): Promise<void> {
    const current = await this.one<SistemaComm>("sistemaComm", id);
    if (!current) throw new Error(`Sistema ${id} no encontrado`);
    await this.put("sistemaComm", {
      ...current, ...data, version: current.version + 1, updatedAt: new Date().toISOString(), updatedBy: userEmail,
    });
  }

  async deleteSistema(id: string): Promise<void> {
    const subs = (await this.all<SubsistemaComm>("subsistemaComm")).filter((s) => s.sistemaId === id);
    await this.del("subsistemaComm", subs.map((s) => s.id));
    await this.del("sistemaComm", [id]);
  }

  // ── Subsistemas ──────────────────────────────────────────────────
  async listSubsistemas(sistemaId: string): Promise<SubsistemaComm[]> {
    return (await this.all<SubsistemaComm>("subsistemaComm"))
      .filter((s) => s.sistemaId === sistemaId)
      .sort((a, b) => a.codigo.localeCompare(b.codigo, undefined, { numeric: true }));
  }

  async createSubsistema(
    data: Omit<SubsistemaComm, "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "createdBy" | "updatedBy">,
    userEmail: string,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    await this.ins("subsistemaComm", {
      ...data, id, tenantId: this.tenantId, version: 1,
      createdAt: now, updatedAt: now, createdBy: userEmail, updatedBy: userEmail,
    });
    return id;
  }

  async updateSubsistema(id: string, data: Partial<SubsistemaComm>, userEmail: string): Promise<void> {
    const current = await this.one<SubsistemaComm>("subsistemaComm", id);
    if (!current) throw new Error(`Subsistema ${id} no encontrado`);
    await this.put("subsistemaComm", {
      ...current, ...data, version: current.version + 1, updatedAt: new Date().toISOString(), updatedBy: userEmail,
    });
  }

  async deleteSubsistema(id: string): Promise<void> {
    await this.del("subsistemaComm", [id]);
  }

  // ── Pendientes ───────────────────────────────────────────────────
  async listPendientes(listadoId: string): Promise<PendienteComm[]> {
    return (await this.all<PendienteComm>("pendienteComm"))
      .filter((p) => p.listadoId === listadoId)
      .sort((a, b) => a.numItem - b.numItem);
  }

  async countAbiertosByAfe(afeId: string): Promise<number> {
    const listados = (await this.all<ListadoPendientes>("listadoPendientes")).filter((l) => l.afeId === afeId);
    const listadoIds = new Set(listados.map((l) => l.id));
    if (listadoIds.size === 0) return 0;
    const pendientes = await this.all<PendienteComm>("pendienteComm");
    return pendientes.filter((p) => listadoIds.has(p.listadoId) && p.estado === "ABIERTO").length;
  }

  async getPendiente(id: string): Promise<PendienteComm | null> {
    return this.one<PendienteComm>("pendienteComm", id);
  }

  async createPendiente(
    data: Omit<PendienteComm, "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "createdBy" | "updatedBy" | "numItem">,
    userEmail: string,
  ): Promise<string> {
    const id = crypto.randomUUID();
    const now = new Date().toISOString();
    const numItem = await this.siguienteNumItem(data.listadoId);
    await this.ins("pendienteComm", {
      ...data, id, numItem, tenantId: this.tenantId, version: 1,
      createdAt: now, updatedAt: now, createdBy: userEmail, updatedBy: userEmail,
    });
    return id;
  }

  async updatePendiente(id: string, data: Partial<PendienteComm>, expectedVersion: number, userEmail: string): Promise<void> {
    const current = await this.getPendiente(id);
    if (!current) throw new Error(`Pendiente ${id} no encontrado`);
    if (current.version !== expectedVersion) throw new OptimisticLockError(id, expectedVersion, current.version);
    await this.put("pendienteComm", {
      ...current, ...data, version: current.version + 1, updatedAt: new Date().toISOString(), updatedBy: userEmail,
    });
  }

  async cerrarPendiente(id: string, fotoCierre: string | undefined, userEmail: string): Promise<void> {
    const current = await this.getPendiente(id);
    if (!current) throw new Error(`Pendiente ${id} no encontrado`);
    if (current.estado === "CERRADO") return;
    const now = new Date().toISOString();
    await this.put("pendienteComm", {
      ...current, estado: "CERRADO", fechaCierreReal: now.slice(0, 10),
      ...(fotoCierre !== undefined && { fotoCierre }),
      version: current.version + 1, updatedAt: now, updatedBy: userEmail,
    });
  }

  async reabrirPendiente(id: string, userEmail: string): Promise<void> {
    const current = await this.getPendiente(id);
    if (!current) throw new Error(`Pendiente ${id} no encontrado`);
    if (current.estado === "ABIERTO") return;
    const now = new Date().toISOString();
    const updated: PendienteComm = {
      ...current, estado: "ABIERTO", version: current.version + 1, updatedAt: now, updatedBy: userEmail,
    };
    delete updated.fechaCierreReal;
    await this.put("pendienteComm", updated);
  }

  async deletePendiente(id: string): Promise<void> {
    await this.del("pendienteComm", [id]);
  }

  async bulkCreatePendientes(
    listadoId: string,
    pendientes: Array<
      Omit<PendienteComm, "id" | "tenantId" | "createdAt" | "updatedAt" | "version" | "createdBy" | "updatedBy" | "numItem" | "listadoId">
    >,
    userEmail: string,
  ): Promise<number> {
    const now = new Date().toISOString();
    let nextItem = await this.siguienteNumItem(listadoId);
    const docs: PendienteComm[] = pendientes.map((p) => ({
      ...p, id: crypto.randomUUID(), listadoId, numItem: nextItem++,
      tenantId: this.tenantId, version: 1,
      createdAt: now, updatedAt: now, createdBy: userEmail, updatedBy: userEmail,
    }));
    for (const d of docs) await this.ins("pendienteComm", d);
    return docs.length;
  }

  private async siguienteNumItem(listadoId: string): Promise<number> {
    const todos = await this.listPendientes(listadoId);
    return todos.reduce((m, p) => Math.max(m, p.numItem), 0) + 1;
  }
}
